import { Enum, TableInsertModel, TableRowModel, TableUpdateModel } from '@examdojo/supabase';
import { DailyHoursMap } from '../payments.model';

export type PayoutRequestHttpModel = Pick<
  TableRowModel<'payout_requests', 'billing'>,
  | 'id'
  | 'user_id'
  | 'period'
  | 'status'
  | 'status_reason'
  | 'hours'
  | 'extra_bonus_in_eur'
  | 'extra_bonus_reason'
  | 'final_payout_in_eur'
  | 'target_account_id'
  | 'transfer_id'
  | 'transaction_id'
  | 'created_at'
  | 'updated_at'
  | 'attachments'
  | 'payout_rate_id'
  | 'performance_bonus_in_eur'
>;
export type PayoutRequestInsertModel = TableInsertModel<'payout_requests', 'billing'>;
export type PayoutRequestUpdateModel = TableUpdateModel<'payout_requests', 'billing'>;

export interface PayoutRequestListViewHttpModel extends PayoutRequestHttpModel {
  user_name: string;
  user_email: string;
  current_hourly_rate: number;
}

export type PayoutRequestStoreModel = Omit<PayoutRequestHttpModel, 'hours'> & {
  hours: DailyHoursMap;
};

export interface PayoutRequestUIModel extends PayoutRequestStoreModel {
  user_name?: string;
  user_email?: string;
  current_hourly_rate?: number;

  isEditable: boolean;
  canSubmit: boolean;
  statusLabel: string;
  totalHours: number;

  /** Estimated payout based on hours worked and current hourly rate */
  estimatedPayout: number;

  /** The hourly rate that was applied for this period's calculation */
  appliedHourlyRate: number;

  /** Calculated final payout (for DRAFT) or from database (for other statuses) */
  calculatedFinalPayout: number | null;
}

export interface PayoutRequestData {
  period: string;
  hours: DailyHoursMap;
}

export type PayoutRequestStatus = Enum<'payout_request_status', 'billing'>;

export const PAYOUT_REQUEST_STATUS_LABELS: Record<PayoutRequestStatus, string> = {
  DRAFT: 'Draft',
  WAITING_FOR_APPROVAL: 'Waiting for Approval',
  REJECTED: 'Rejected',
  APPROVED: 'Approved',
  PAYMENT_WAITING_FOR_APPROVAL: 'Payment Pending',
  PAYMENT_IN_PROGRESS: 'Payment in Progress',
  PAYMENT_SUCCEEDED: 'Payment Completed',
  PAYMENT_FAILED: 'Payment Failed',
};

export const PAYMENT_STATUS_BADGE_COLOR_CLASSES: Record<PayoutRequestStatus, string> = {
  DRAFT: 'bg-amber-100 text-amber-800',
  WAITING_FOR_APPROVAL: 'bg-sky-100 text-sky-800',
  REJECTED: 'bg-red-100 text-red-800',
  APPROVED: 'bg-green-100 text-green-800',
  PAYMENT_WAITING_FOR_APPROVAL: 'bg-sky-100 text-sky-800',
  PAYMENT_IN_PROGRESS: 'bg-sky-100 text-sky-800',
  PAYMENT_SUCCEEDED: 'bg-green-100 text-green-800',
  PAYMENT_FAILED: 'bg-red-100 text-red-800',
};

export const PAYMENT_STATUS_DOT_COLOR_CLASSES: Record<PayoutRequestStatus, string> = {
  DRAFT: 'bg-yellow-500',
  WAITING_FOR_APPROVAL: 'bg-sky-500',
  REJECTED: 'bg-red-500',
  APPROVED: 'bg-green-500',
  PAYMENT_WAITING_FOR_APPROVAL: 'bg-sky-500',
  PAYMENT_IN_PROGRESS: 'bg-sky-500',
  PAYMENT_SUCCEEDED: 'bg-green-500',
  PAYMENT_FAILED: 'bg-red-500',
};
